import React, { useState, useEffect, useCallback } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button } from 'reactstrap';
import {
  CHART_REGISTRY,
  type ModuleDef,
  type DynamicChartConfig,
  type DynChartType,
} from './ChartRegistry';
import DynamicChart from './DynamicChart';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onAdd: (config: DynamicChartConfig, isShared: boolean) => void;
}

const CHART_TYPES: { value: DynChartType; label: string; icon: string; desc: string; requiresRawFetcher?: boolean }[] = [
  { value: 'bar-h',     label: 'Bar (H)',    icon: '📊', desc: 'Horizontal bars — good for named categories' },
  { value: 'bar-v',     label: 'Bar (V)',    icon: '📈', desc: 'Vertical bars — good for comparisons' },
  { value: 'donut',     label: 'Donut',      icon: '🍩', desc: 'Ring chart — good for proportions' },
  { value: 'table',     label: 'Table',      icon: '🗃️', desc: 'Sortable breakdown with percentages' },
  { value: 'number',    label: 'Big Number', icon: '🔢', desc: 'Single total count — good for KPIs' },
  { value: 'raw-table', label: 'Raw Table',  icon: '📄', desc: 'Show all records with selectable fields', requiresRawFetcher: true },
];

const WIDTH_OPTIONS = [
  { label: '1/3',  value: 4  },
  { label: '1/2',  value: 6  },
  { label: '2/3',  value: 8  },
  { label: 'Full', value: 12 },
];

const ACCENT = '#5a9b82';

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

const allCategories = () => ['All', ...Array.from(new Set(CHART_REGISTRY.map(m => m.category)))];

const pill = (active: boolean): React.CSSProperties => ({
  padding: '4px 12px', borderRadius: 20, fontSize: 12, cursor: 'pointer', fontWeight: active ? 700 : 400,
  background: active ? ACCENT : '#F3F4F6',
  border: `1px solid ${active ? ACCENT : '#E5E7EB'}`,
  color: active ? '#fff' : '#6B7280',
});

const stepBadge = (active: boolean, done: boolean): React.CSSProperties => ({
  width: 28, height: 28, borderRadius: '50%', fontSize: 12, fontWeight: 700,
  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
  background: done ? ACCENT : active ? '#EFF6FF' : '#F3F4F6',
  color: done ? '#fff' : active ? ACCENT : '#9CA3AF',
  border: `2px solid ${done || active ? ACCENT : '#E5E7EB'}`,
});

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────

export default function DynamicChartBuilder({ isOpen, onClose, onAdd }: Props) {
  const [step, setStep]                   = useState<1 | 2>(1);
  const [selMod, setSelMod]               = useState<ModuleDef | null>(null);
  const [dimension, setDimension]         = useState('');
  const [filterVals, setFilterVals]       = useState<Record<string, string>>({});
  const [filterOpts, setFilterOpts]       = useState<Record<string, { value: string; label: string }[]>>({});
  const [chartType, setChartType]         = useState<DynChartType>('bar-h');
  const [width, setWidth]                 = useState(6);
  const [label, setLabel]                 = useState('');
  const [catFilter, setCatFilter]         = useState('All');
  const [search, setSearch]               = useState('');
  const [selectedFields, setSelectedFields] = useState<string[]>([]);
  const [isShared, setIsShared]           = useState(false);

  const categories = allCategories();

  const filteredMods = CHART_REGISTRY.filter(m =>
    (catFilter === 'All' || m.category === catFilter) &&
    (!search || m.label.toLowerCase().includes(search.toLowerCase()) ||
     m.description.toLowerCase().includes(search.toLowerCase()))
  );

  // Load async select options when a module is chosen
  const handlePickModule = useCallback(async (mod: ModuleDef) => {
    setSelMod(mod);
    setDimension(mod.dimensions[0]?.field ?? '');
    setFilterVals({});
    setChartType('bar-h');
    setLabel(mod.label);
    setSelectedFields(mod.allowedFields?.map(f => f.id) ?? []);
    setStep(2);

    const opts: Record<string, { value: string; label: string }[]> = {};
    for (const f of mod.filters) {
      if (f.optionsFetcher) {
        try { opts[f.field] = await f.optionsFetcher(); }
        catch  { opts[f.field] = []; }
      } else if (f.options) {
        opts[f.field] = f.options;
      }
    }
    setFilterOpts(opts);
  }, []);

  const previewConfig: DynamicChartConfig | null = selMod && (chartType === 'raw-table' || dimension) ? {
    id: '__preview__',
    label,
    module: selMod.id,
    dimension,
    filters: Object.entries(filterVals)
      .filter(([, v]) => v)
      .map(([field, value]) => ({ field, value })),
    chartType,
    width,
    selectedFields: chartType === 'raw-table' ? selectedFields : undefined,
  } : null;

  const handleAdd = () => {
    if (!previewConfig || !label.trim()) return;
    onAdd({ ...previewConfig, id: `dyn-${Date.now()}`, label: label.trim() }, isShared);
    handleClose();
  };

  const handleClose = () => {
    setStep(1);
    setSelMod(null);
    setDimension('');
    setFilterVals({});
    setFilterOpts({});
    setLabel('');
    setSearch('');
    setSelectedFields([]);
    setIsShared(false);
    onClose();
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <Modal isOpen={isOpen} toggle={handleClose} size="xl" style={{maxWidth:1080}}>
      {/* ── Header ────────────────────────────────────────────────────────── */}
      <ModalHeader toggle={handleClose} style={{borderBottom:`2px solid ${ACCENT}`, padding:'14px 20px'}}>
        <div style={{display:'flex', alignItems:'center', gap:16}}>
          <div>
            <div style={{fontSize:16, fontWeight:700, color:'#111827'}}>✨ Create Dynamic Chart</div>
            <div style={{fontSize:12, color:'#9CA3AF', fontWeight:400, marginTop:1}}>
              Build a real-time chart from any data module in the platform
            </div>
          </div>
          {/* Step indicator */}
          <div style={{display:'flex', alignItems:'center', gap:8, marginLeft:24}}>
            {([{n:1, label:'Pick module'}, {n:2, label:'Configure'}] as const).map(({n, label:l}) => (
              <React.Fragment key={n}>
                <div style={{display:'flex', alignItems:'center', gap:6}}>
                  <div style={stepBadge(step === n, step > n)}>{step > n ? '✓' : n}</div>
                  <span style={{fontSize:12, color: step === n ? ACCENT : step > n ? '#374151' : '#9CA3AF', fontWeight: step === n ? 600 : 400}}>{l}</span>
                </div>
                {n < 2 && <div style={{width:24, height:1, background:'#E5E7EB'}}/>}
              </React.Fragment>
            ))}
          </div>
        </div>
      </ModalHeader>

      {/* ── Body ──────────────────────────────────────────────────────────── */}
      <ModalBody style={{padding:0, minHeight:520}}>

        {/* STEP 1 — Module picker */}
        {step === 1 && (
          <div style={{padding:24}}>
            {/* Search + category pills */}
            <div style={{display:'flex', gap:10, marginBottom:20, flexWrap:'wrap', alignItems:'center'}}>
              <div style={{position:'relative', flex:1, minWidth:200}}>
                <span style={{position:'absolute', left:10, top:'50%', transform:'translateY(-50%)', color:'#9CA3AF', fontSize:14}}>🔍</span>
                <input
                  placeholder="Search modules…"
                  value={search}
                  onChange={e => setSearch(e.target.value)}
                  style={{width:'100%', padding:'8px 12px 8px 32px', borderRadius:8, border:'1px solid #E5E7EB', fontSize:13, outline:'none', boxSizing:'border-box'}}/>
              </div>
              <div style={{display:'flex', gap:6, flexWrap:'wrap'}}>
                {categories.map(cat => (
                  <button key={cat} onClick={() => setCatFilter(cat)} style={pill(catFilter === cat)}>{cat}</button>
                ))}
              </div>
            </div>

            {/* Module grid */}
            <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(210px, 1fr))', gap:14}}>
              {filteredMods.map(mod => (
                <div key={mod.id}
                  onClick={() => handlePickModule(mod)}
                  onMouseEnter={e => { e.currentTarget.style.borderColor=ACCENT; e.currentTarget.style.background='#F0FDF4'; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor='#E5E7EB'; e.currentTarget.style.background='#fff'; }}
                  style={{padding:18, borderRadius:10, border:'2px solid #E5E7EB', cursor:'pointer', background:'#fff', transition:'all 0.15s'}}>
                  <div style={{fontSize:30, marginBottom:10}}>{mod.icon}</div>
                  <div style={{fontSize:13, fontWeight:700, color:'#111827', marginBottom:4}}>{mod.label}</div>
                  <div style={{fontSize:11, color:'#9CA3AF', lineHeight:'16px', marginBottom:10}}>{mod.description}</div>
                  <div style={{display:'flex', alignItems:'center', gap:8}}>
                    <span style={{fontSize:10, padding:'2px 8px', borderRadius:12, background:'#EFF6FF', color:'#3B82F6', fontWeight:600}}>{mod.category}</span>
                    <span style={{fontSize:10, color:'#D1D5DB'}}>{mod.dimensions.length}D · {mod.filters.length}F</span>
                  </div>
                </div>
              ))}
              {filteredMods.length === 0 && (
                <div style={{gridColumn:'1/-1', textAlign:'center', padding:'48px 0', color:'#9CA3AF', fontSize:13}}>
                  No modules match your search
                </div>
              )}
            </div>
          </div>
        )}

        {/* STEP 2 — Configure + Preview */}
        {step === 2 && selMod && (
          <div style={{display:'flex', height:'100%', minHeight:480}}>

            {/* LEFT — configuration panel */}
            <div style={{width:310, borderRight:'1px solid #F3F4F6', padding:'20px 20px', overflowY:'auto', flexShrink:0}}>
              <button
                onClick={() => setStep(1)}
                style={{background:'none', border:'none', cursor:'pointer', color:'#6B7280', fontSize:12, marginBottom:18, display:'flex', alignItems:'center', gap:4, padding:0}}>
                ← Back to modules
              </button>

              {/* Module info */}
              <div style={{padding:'10px 12px', borderRadius:8, background:'#F0FDF4', border:`1px solid ${ACCENT}30`, marginBottom:20, display:'flex', alignItems:'center', gap:10}}>
                <span style={{fontSize:22}}>{selMod.icon}</span>
                <div>
                  <div style={{fontSize:13, fontWeight:700, color:'#374151'}}>{selMod.label}</div>
                  <div style={{fontSize:10, color:'#6B7280'}}>{selMod.category}</div>
                </div>
              </div>

              {/* Chart name */}
              <div style={{marginBottom:16}}>
                <label style={{fontSize:11, fontWeight:600, color:'#6B7280', display:'block', marginBottom:5, textTransform:'uppercase', letterSpacing:0.5}}>Chart Name</label>
                <input
                  value={label}
                  onChange={e => setLabel(e.target.value)}
                  placeholder="e.g. Compliance by Status"
                  style={{width:'100%', padding:'7px 10px', borderRadius:8, border:'1px solid #E5E7EB', fontSize:13, outline:'none', color:'#374151', boxSizing:'border-box'}}/>
              </div>

              {/* Dimension — hidden in raw-table mode */}
              {chartType !== 'raw-table' && (
                <div style={{marginBottom:16}}>
                  <label style={{fontSize:11, fontWeight:600, color:'#6B7280', display:'block', marginBottom:5, textTransform:'uppercase', letterSpacing:0.5}}>Group By</label>
                  <select
                    value={dimension}
                    onChange={e => setDimension(e.target.value)}
                    style={{width:'100%', padding:'7px 10px', borderRadius:8, border:'1px solid #E5E7EB', fontSize:13, color:'#374151', background:'#fff', cursor:'pointer', boxSizing:'border-box'}}>
                    {selMod.dimensions.map(d => (
                      <option key={d.field} value={d.field}>{d.label}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* Field picker — only in raw-table mode */}
              {chartType === 'raw-table' && selMod.allowedFields && selMod.allowedFields.length > 0 && (
                <div style={{marginBottom:16}}>
                  <div style={{display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:6}}>
                    <label style={{fontSize:11, fontWeight:600, color:'#6B7280', textTransform:'uppercase', letterSpacing:0.5}}>Columns to Show</label>
                    <div style={{display:'flex', gap:6}}>
                      <button onClick={() => setSelectedFields(selMod.allowedFields!.map(f => f.id))}
                        style={{fontSize:10, color:ACCENT, background:'none', border:'none', cursor:'pointer', padding:0, fontWeight:600}}>All</button>
                      <button onClick={() => setSelectedFields([])}
                        style={{fontSize:10, color:'#9CA3AF', background:'none', border:'none', cursor:'pointer', padding:0}}>None</button>
                    </div>
                  </div>
                  <div style={{display:'flex', flexDirection:'column', gap:4, padding:'8px 10px', background:'#F9FAFB', borderRadius:8, border:'1px solid #F3F4F6'}}>
                    {selMod.allowedFields.map(f => {
                      const checked = selectedFields.includes(f.id);
                      return (
                        <label key={f.id} style={{display:'flex', alignItems:'center', gap:8, cursor:'pointer', fontSize:12, color: checked ? '#374151' : '#9CA3AF', fontWeight: checked ? 500 : 400}}>
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={e => setSelectedFields(prev =>
                              e.target.checked ? [...prev, f.id] : prev.filter(id => id !== f.id)
                            )}
                            style={{accentColor: ACCENT, width:13, height:13}}
                          />
                          {f.label}
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Filters */}
              {selMod.filters.length > 0 && (
                <div style={{marginBottom:16}}>
                  <label style={{fontSize:11, fontWeight:600, color:'#6B7280', display:'block', marginBottom:8, textTransform:'uppercase', letterSpacing:0.5}}>Filters</label>
                  {selMod.filters.map(f => {
                    const opts = filterOpts[f.field] || f.options || [];
                    return (
                      <div key={f.field} style={{marginBottom:12}}>
                        <div style={{fontSize:12, color:'#374151', marginBottom:4, fontWeight:500}}>{f.label}</div>
                        {f.type === 'select' && (
                          <select
                            value={filterVals[f.field] || ''}
                            onChange={e => setFilterVals(p => ({ ...p, [f.field]: e.target.value }))}
                            style={{width:'100%', padding:'6px 10px', borderRadius:7, border:'1px solid #E5E7EB', fontSize:12, color:'#374151', background:'#fff', cursor:'pointer', boxSizing:'border-box'}}>
                            <option value="">All (no filter)</option>
                            {opts.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                          </select>
                        )}
                        {f.type === 'text' && (
                          <input
                            value={filterVals[f.field] || ''}
                            onChange={e => setFilterVals(p => ({ ...p, [f.field]: e.target.value }))}
                            placeholder={`Filter by ${f.label.toLowerCase()}…`}
                            style={{width:'100%', padding:'6px 10px', borderRadius:7, border:'1px solid #E5E7EB', fontSize:12, color:'#374151', outline:'none', boxSizing:'border-box'}}/>
                        )}
                        {f.type === 'date' && (
                          <input
                            type="date"
                            value={filterVals[f.field] || ''}
                            onChange={e => setFilterVals(p => ({ ...p, [f.field]: e.target.value }))}
                            style={{width:'100%', padding:'6px 10px', borderRadius:7, border:'1px solid #E5E7EB', fontSize:12, color:'#374151', outline:'none', boxSizing:'border-box'}}/>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Chart type */}
              <div style={{marginBottom:16}}>
                <label style={{fontSize:11, fontWeight:600, color:'#6B7280', display:'block', marginBottom:8, textTransform:'uppercase', letterSpacing:0.5}}>Chart Type</label>
                <div style={{display:'flex', flexDirection:'column', gap:6}}>
                  {CHART_TYPES
                    .filter(ct => !ct.requiresRawFetcher || (selMod.allowedFields && selMod.rawFetcher))
                    .map(ct => (
                    <button key={ct.value}
                      onClick={() => setChartType(ct.value)}
                      style={{
                        padding:'8px 12px', borderRadius:8, fontSize:12, textAlign:'left', cursor:'pointer',
                        border:`2px solid ${chartType === ct.value ? ACCENT : '#E5E7EB'}`,
                        background: chartType === ct.value ? '#F0FDF4' : '#fff',
                        color: chartType === ct.value ? ACCENT : '#374151',
                        display:'flex', alignItems:'center', gap:10, fontWeight: chartType === ct.value ? 700 : 400,
                      }}>
                      <span style={{fontSize:16}}>{ct.icon}</span>
                      <div>
                        <div style={{fontWeight:600}}>{ct.label}</div>
                        <div style={{fontSize:10, color:'#9CA3AF', fontWeight:400}}>{ct.desc}</div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Width */}
              <div style={{marginBottom:16}}>
                <label style={{fontSize:11, fontWeight:600, color:'#6B7280', display:'block', marginBottom:8, textTransform:'uppercase', letterSpacing:0.5}}>Dashboard Width</label>
                <div style={{display:'flex', gap:6}}>
                  {WIDTH_OPTIONS.map(wo => (
                    <button key={wo.value}
                      onClick={() => setWidth(wo.value)}
                      style={{
                        flex:1, padding:'6px 4px', borderRadius:7, fontSize:12, fontWeight: width === wo.value ? 700 : 400,
                        border:`1px solid ${width === wo.value ? ACCENT : '#E5E7EB'}`,
                        background: width === wo.value ? ACCENT : '#fff',
                        color: width === wo.value ? '#fff' : '#6B7280', cursor:'pointer',
                      }}>
                      {wo.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Share with team */}
              <div style={{padding:'10px 12px', borderRadius:8, background: isShared ? '#F0FDF4' : '#F9FAFB', border:`1px solid ${isShared ? ACCENT+'40' : '#E5E7EB'}`, cursor:'pointer'}}
                onClick={() => setIsShared(v => !v)}>
                <label style={{display:'flex', alignItems:'flex-start', gap:10, cursor:'pointer'}}>
                  <input
                    type="checkbox"
                    checked={isShared}
                    onChange={e => setIsShared(e.target.checked)}
                    style={{accentColor: ACCENT, width:15, height:15, marginTop:2, flexShrink:0}}
                    onClick={e => e.stopPropagation()}
                  />
                  <div>
                    <div style={{fontSize:12, fontWeight:600, color: isShared ? ACCENT : '#374151'}}>
                      Share with my team
                    </div>
                    <div style={{fontSize:10, color:'#9CA3AF', marginTop:2, lineHeight:'15px'}}>
                      When checked, this chart is visible to all users in your same group and function. Otherwise only you can see it.
                    </div>
                  </div>
                </label>
              </div>
            </div>

            {/* RIGHT — live preview */}
            <div style={{flex:1, padding:20, background:'#F9FAFB', display:'flex', flexDirection:'column', minWidth:0}}>
              <div style={{fontSize:11, fontWeight:700, color:'#9CA3AF', marginBottom:14, textTransform:'uppercase', letterSpacing:1}}>
                Live Preview
              </div>
              {previewConfig ? (
                <div style={{background:'#fff', borderRadius:10, flex:1, padding:16, boxShadow:'0 1px 6px rgba(0,0,0,0.06)', overflow:'auto'}}>
                  <DynamicChart config={previewConfig} compact/>
                </div>
              ) : (
                <div style={{display:'flex', alignItems:'center', justifyContent:'center', flex:1, color:'#D1D5DB', fontSize:13, flexDirection:'column', gap:8}}>
                  <div style={{fontSize:32}}>📊</div>
                  Configure the settings on the left to see a live preview
                </div>
              )}
            </div>
          </div>
        )}
      </ModalBody>

      {/* ── Footer ────────────────────────────────────────────────────────── */}
      {step === 2 && (
        <ModalFooter style={{borderTop:'1px solid #E5E7EB', display:'flex', justifyContent:'space-between', padding:'12px 20px'}}>
          <Button color="secondary" outline size="sm" onClick={() => setStep(1)}>← Change Module</Button>
          <Button
            size="sm"
            disabled={!label.trim() || (chartType !== 'raw-table' && !dimension) || (chartType === 'raw-table' && selectedFields.length === 0)}
            onClick={handleAdd}
            style={{background: ACCENT, border:'none', color:'#fff', fontWeight:600, padding:'8px 20px'}}>
            + Add to Dashboard
          </Button>
        </ModalFooter>
      )}
    </Modal>
  );
}
